import { Alert, Badge, Spinner } from '@1k5-tech-es/grado-react';

import type { Period, QuarterSummary, ReportableQuarter } from '../../api/types';
import { monthName } from '../../lib/format';
import { quarterLabel } from './quarters';

/** Name of the selected quarter and what will be generated. */
export function QuarterHeading({ quarter, summary }: { quarter: ReportableQuarter; summary: QuarterSummary | undefined }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">Trimestre elegido</div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-semibold">{quarterLabel(quarter)}</h3>
        {quarter.inProgress && <Badge tone="warning">Mes en curso</Badge>}
      </div>
      <p className="mt-0.5 text-sm text-muted-foreground">Informe de {monthName(quarter.end.month)}</p>
      {summary && (
        <p className="mt-4 flex items-baseline gap-2">
          <span className="text-3xl font-bold text-primary-text tabular-nums">{summary.advisorCount}</span>
          <span className="text-sm text-muted-foreground">informes, uno por asesor con datos en el trimestre</span>
        </p>
      )}
      {quarter.inProgress && (
        <Alert tone="warning" title={`${monthName(quarter.end.month)} todavía no ha terminado`} className="mt-3">
          Sus cifras pueden cambiar antes del envío.
        </Alert>
      )}
    </div>
  );
}

/** The three reported months plus the next month's banner, with how complete each one is. */
export function QuarterTimeline({ summary }: { summary: QuarterSummary | undefined }) {
  return (
    <div>
      <ol className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {summary ? (
          <>
            {summary.months.map((m, i) => (
              <MonthStep key={m.period.month} period={m.period} caption={`Mes ${i + 1}`}>
                {i > 0 && <Coverage label="Actividad" value={m.withActivity} total={summary.advisorCount} />}
                <Coverage label="Mínimo" value={m.withMinimum} total={summary.advisorCount} />
                <Coverage label="Objetivo" value={m.withTarget} total={summary.advisorCount} />
              </MonthStep>
            ))}
            <MonthStep period={summary.nextMonth.period} caption="Banner" dashed>
              <Coverage label="Mínimo" value={summary.nextMonth.withMinimum} total={summary.advisorCount} />
              <Coverage label="Objetivo" value={summary.nextMonth.withTarget} total={summary.advisorCount} />
            </MonthStep>
          </>
        ) : (
          <li className="col-span-full flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner size="sm" decorative /> Analizando los datos del trimestre…
          </li>
        )}
      </ol>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Los KPIs comparan los dos últimos meses; el primero solo suma ventas y mínimos al balance del Art. 2.8.
      </p>
    </div>
  );
}

function MonthStep({
  period,
  caption,
  dashed,
  children,
}: {
  period: Period;
  caption: string;
  dashed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li
      className={`rounded-xl px-3 py-2.5 ${
        dashed ? 'border border-dashed border-primary-border bg-card' : 'border border-primary-border bg-primary-subtle'
      }`}
    >
      <div className="text-[10px] font-semibold tracking-wider text-primary-text uppercase">{caption}</div>
      <div className="text-sm font-semibold">
        {monthName(period.month)} <span className="font-normal text-muted-foreground">{period.year}</span>
      </div>
      <dl className="mt-1.5 space-y-0.5">{children}</dl>
    </li>
  );
}

function Coverage({ label, value, total }: { label: string; value: number; total: number }) {
  const complete = total > 0 && value === total;
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`font-semibold tabular-nums ${complete ? 'text-success-text' : value === 0 ? 'text-muted-foreground' : 'text-warning-text'}`}>
        {complete ? '✓' : `${value}/${total}`}
      </dd>
    </div>
  );
}
